import { APP_BUILDINGS, LOOT, WALL } from "./config.ts";
import { computeLevels } from "./levels.ts";
import { clampInt, londonDate, seededUnit } from "./time.ts";
import type {
  AppId,
  BuildingId,
  CollectResult,
  RaidResult,
  VillageMetrics,
  VillageState,
  TickResult,
} from "./types.ts";
import { APP_IDS, BUILDING_IDS } from "./types.ts";

function emptyLevels(): Record<BuildingId, number> {
  const levels = {} as Record<BuildingId, number>;
  for (const id of BUILDING_IDS) {
    levels[id] = 0;
  }
  return levels;
}

export function blankState(date: string): VillageState {
  return {
    date,
    levels: emptyLevels(),
    gold: LOOT.startingGold,
    crystals: LOOT.startingCrystals,
    wallHp: WALL.startingHp,
    decorations: [],
    lastDailyActionsDone: false,
    justLeveled: [],
    raid: null,
    collect: null,
    sprint: null,
    bugsFixedSeen: 0,
  };
}

function carryLoot(value: number): number {
  return clampInt(value, 0, LOOT.maxCarry);
}

function sumTankLevels(levels: Record<BuildingId, number>): number {
  let sum = 0;
  for (const appId of APP_IDS) {
    const buildingId = APP_BUILDINGS[appId as AppId];
    sum += levels[buildingId];
  }
  return sum;
}

export function collectYield(levels: Record<BuildingId, number>): CollectResult {
  const gold = LOOT.baseCollectGold + levels.goldMine * LOOT.goldPerMineLevel;
  const crystals = LOOT.baseCollectCrystals + sumTankLevels(levels) * LOOT.crystalsPerTankLevel;
  return { happened: true, gold, crystals };
}

export function raidOutcome(date: string, gold: number, crystals: number): RaidResult {
  const unit = seededUnit(`raid:${date}`);
  const wallDamage = Math.round(
    WALL.raidDamageMin + unit * (WALL.raidDamageMax - WALL.raidDamageMin),
  );
  return {
    happened: true,
    goldStolen: Math.floor(gold * WALL.raidStealRatio),
    crystalsStolen: Math.floor(crystals * WALL.raidStealRatio),
    wallDamage,
  };
}

function applyBugRepairs(wallHp: number, previousBugs: number, bugsFixed: number): number {
  const gained = Math.max(0, bugsFixed - previousBugs);
  return clampInt(wallHp + gained * WALL.hpPerBugFixed, 0, WALL.maxHp);
}

function detectLevelUps(
  previous: Record<BuildingId, number> | null,
  next: Record<BuildingId, number>,
): BuildingId[] {
  if (!previous) {
    return [];
  }
  const leveled: BuildingId[] = [];
  for (const id of BUILDING_IDS) {
    if (next[id] > previous[id]) {
      leveled.push(id);
    }
  }
  return leveled;
}

export function tick(
  metrics: VillageMetrics,
  yesterday: VillageState | null,
  at: Date = new Date(),
  missingMetrics: string[] = [],
): TickResult {
  const today = metrics.date || londonDate(at);
  const previous = yesterday;
  const sameDay = previous?.date === today;

  let gold = previous?.gold ?? LOOT.startingGold;
  let crystals = previous?.crystals ?? LOOT.startingCrystals;
  let wallHp = previous?.wallHp ?? WALL.startingHp;
  const decorations = previous?.decorations.slice() ?? [];
  const sprint = previous?.sprint ?? null;
  const bugsFixedSeen = previous?.bugsFixedSeen ?? 0;

  wallHp = applyBugRepairs(wallHp, bugsFixedSeen, metrics.bugsFixed);

  let collect: CollectResult | null = sameDay ? (previous?.collect ?? null) : null;
  let raid: RaidResult | null = sameDay ? (previous?.raid ?? null) : null;

  if (!sameDay) {
    if (metrics.dailyActionsDoneYesterday) {
      collect = collectYield(computeLevels(metrics, wallHp).levels);
      gold = carryLoot(gold + collect.gold);
      crystals = carryLoot(crystals + collect.crystals);
      raid = { happened: false, goldStolen: 0, crystalsStolen: 0, wallDamage: 0 };
    } else {
      raid = raidOutcome(today, gold, crystals);
      gold = carryLoot(gold - raid.goldStolen);
      crystals = carryLoot(crystals - raid.crystalsStolen);
      wallHp = clampInt(wallHp - raid.wallDamage, 0, WALL.maxHp);
      collect = { happened: false, gold: 0, crystals: 0 };
    }
  }

  const { levels, buildings } = computeLevels(metrics, wallHp);
  const justLeveled = detectLevelUps(previous?.levels ?? null, levels);

  const state: VillageState = {
    date: today,
    levels,
    gold,
    crystals,
    wallHp,
    decorations,
    lastDailyActionsDone: metrics.dailyActionsDoneYesterday,
    justLeveled,
    raid,
    collect,
    sprint,
    bugsFixedSeen: metrics.bugsFixed,
  };

  return { state, buildings, missingMetrics };
}
